"use client";

import { useImageZoom } from "@/hooks/use-image-zoom";

interface ZoomableGalleryImageProps {
  alt: string;
  onError: () => void;
  onLoad: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  sizes: string;
  src: string;
  srcSet?: string;
}

export function ZoomableGalleryImage({
  alt,
  onError,
  onLoad,
  onSwipeLeft,
  onSwipeRight,
  sizes,
  src,
  srcSet,
}: ZoomableGalleryImageProps) {
  const {
    canZoomIn,
    canZoomOut,
    containerRef,
    handleDoubleClick,
    handlePointerDown,
    handlePointerEnd,
    handlePointerMove,
    handleWheel,
    isSwipeTracking,
    resetZoom,
    swipeOffsetX,
    transform,
    zoomIn,
    zoomOut,
    zoomPercentage,
  } = useImageZoom(src, { onSwipeLeft, onSwipeRight });

  return (
    <div
      ref={containerRef}
      className={`relative h-[90vh] w-[90vw] overflow-hidden ${
        canZoomOut ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"
      }`}
      data-zoom-scale={transform.scale}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={handleDoubleClick}
      onPointerCancel={handlePointerEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onWheel={handleWheel}
      style={{ touchAction: "none" }}
    >
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          opacity: Math.max(1 - Math.abs(swipeOffsetX) / 600, 0.65),
          transform: `translate3d(${swipeOffsetX}px, 0, 0)`,
          transition: isSwipeTracking ? "none" : "transform 180ms ease-out, opacity 180ms ease-out",
        }}
      >
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          className="h-full w-full select-none object-contain will-change-transform"
          draggable={false}
          onLoad={onLoad}
          onError={onError}
          style={{
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
          }}
        />
      </div>

      <div
        className="absolute left-1/2 top-4 z-20 flex -translate-x-1/2 items-center overflow-hidden rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="flex size-11 items-center justify-center text-xl sm:size-10 hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Zoom out"
          disabled={!canZoomOut}
          onClick={zoomOut}
        >
          −
        </button>
        <button
          type="button"
          className="h-11 min-w-16 border-x sm:h-10 border-white/15 px-2 text-sm font-medium hover:bg-white/15 disabled:cursor-default"
          aria-label="Reset zoom"
          disabled={!canZoomOut}
          onClick={resetZoom}
        >
          {zoomPercentage}%
        </button>
        <button
          type="button"
          className="flex size-11 items-center justify-center text-xl sm:size-10 hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Zoom in"
          disabled={!canZoomIn}
          onClick={zoomIn}
        >
          +
        </button>
      </div>
    </div>
  );
}
